<template>
  <div
    class="flex bg-neutral-secondary rounded-md h-full"
    :class="[
      vertical ? 'flex-col-reverse justify-end h-full pr-1 pt-1' : 'm-1 p-1',
    ]"
  >
    <div class="h-full overflow-y-scroll">
      <div
        :class="[vertical ? 'pr-1 pt-1' : 'mr-1']"
        class="flex flex-col w-full basis-2/3 2xl:basis-[70%]"
      >
        <NAccordionGroup v-model:model-value="accordionItemIndex">
          <NAccordion
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: `sidebar`,
              component: `accordionBidAsk`,
              opened: isOpenAccordion(0),
            }"
            :title="t('symbol.marketDepth')"
            has-divider
            elevation
          >
            <SymbolPriceChart
              :lower-price-threshold="symbol.lowerPriceThreshold ?? 0"
              :upper-price-threshold="symbol.upperPriceThreshold ?? 0"
              :highest-trade-price="symbol.highestTradePrice ?? 0"
              :lowest-trade-price="symbol.lowestTradePrice ?? 0"
              :reference-price="symbol.referencePrice ?? 0"
              :last-price="symbol.lastTradePrice ?? 0"
              :previous-day-price="symbol.previousDayPrice ?? 0"
              :instrument-type-code="symbol.instrumentTypeCode ?? ''"
              :size="vertical ? 'small' : 'large'"
            />

            <SymbolBidAskTable
              :items="symbol.bidAsk"
              :isin="symbol.isin ?? ''"
              :lower-price-threshold="symbol.lowerPriceThreshold ?? 0"
              :upper-price-threshold="symbol.upperPriceThreshold ?? 0"
              :change-route="true"
              :show-unit="Boolean(vertical)"
              volume-notation-limit="9999999"
              :size="vertical ? 'small' : 'large'"
            />
          </NAccordion>

          <NAccordion
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: `sidebar`,
              component: 'accordionSymbolChart',
              opened: isOpenAccordion(1),
            }"
            :title="t('symbol.charts.title')"
            has-divider
            elevation
          >
            <SymbolChart />
          </NAccordion>

          <NAccordion
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: `sidebar`,
              component: 'accordionIndividualAndFirm',
              opened: isOpenAccordion(2),
            }"
            :title="t('symbol.individualAndFirm')"
            has-divider
            elevation
          >
            <SymbolIndividualFirmInfo
              :symbol="symbol"
              :size="vertical ? 'small' : 'large'"
            />
          </NAccordion>

          <NAccordion
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: `sidebar`,
              component: 'accordionTradeInfo',
              opened: isOpenAccordion(3),
            }"
            :title="t('modules.order.symbol_detail')"
            has-divider
            elevation
          >
            <SymbolTradeInfo
              :symbol="symbol"
              :portfolio-detail="portfolioQuantity"
              :vertical="false"
              :show-bubble="showBubble"
            />
          </NAccordion>
        </NAccordionGroup>
      </div>
    </div>

    <NBox
      v-if="vertical"
      :rounded="false"
      elevation="medium"
      class="px-3 py-2 rounded-t-md z-20"
    >
      <SymbolInfoCard
        hide-chart
        :symbol="symbol"
        :is-market-map="isMarketMap"
      />
    </NBox>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  SymbolBidAskTable,
  SymbolChart,
  SymbolIndividualFirmInfo,
  SymbolInfoCard,
  SymbolPriceChart,
  SymbolTradeInfo,
  SYNBOL_SUMMARY_TOGGLE_BUTTON_ID,
} from '@/modules/symbol';
import { Instrument, PortfolioItem } from '@libs/api-reference';

type SymbolDetailPropsType = {
  vertical?: boolean;
  portfolioSymbols?: PortfolioItem[] | null;
  symbol: Instrument;
  isMarketMap?: boolean;
  showBubble?: boolean;
  isAccordionLayout?: boolean;
};

const props = withDefaults(defineProps<SymbolDetailPropsType>(), {
  vertical: false,
  portfolioSymbols: () => [],
  isMarketMap: false,
  showBubble: false,
  isAccordionLayout: false,
});

const portfolioQuantity = computed(() => {
  if (props.portfolioSymbols) {
    const symbol = props.portfolioSymbols?.find(
      (portfolioSymbol) =>
        portfolioSymbol.instrumentIsin?.substring(0, 11) ===
        props.symbol.isin?.substring(0, 11)
    );

    return {
      quantity: symbol?.quantity ?? 0,
      percentageValue: symbol?.returnValuePercentage ?? 0,
    };
  }

  return null;
});

const { t } = useI18n();

const accordionItemIndex = ref([0, 1, 2, 3]);

const isOpenAccordion = (index) => {
  return accordionItemIndex.value.includes(index);
};
</script>
